import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {clsx} from 'clsx'
import {ActionList, ActionMenu, Label, RelativeTime} from '@primer/react'
import {PullRequestState} from '../types'
import {useCallback, useEffect, useRef} from 'react'
import styles from './PullRequestMiniHeader.module.css'
import {StateIcon} from './StateIcon'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {getRouteConfig} from '../routes/route-manager'
import {useMergeStatusButtonData, type ValidatedMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {MergeStatusPanel} from './toolbar/MergeStatusPanel'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {OpenOverviewPanelButton} from './toolbar/OpenOverviewPanelButton'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import type {HeaderPageData} from '../page-data/payloads/header'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import type {PullRequestHeaderProps} from './PullRequestHeader'
import {useHydrateMarkerCounts} from '../hooks/use-hydrate-marker-counts'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureState} from '../hooks/use-feature-state'
import {FeatureState} from '../page-data/payloads/header'
import {isLoggedIn, getCurrentUserLogin} from '@github-ui/client-env'
import {OpenAgentsPanelButton} from './toolbar/OpenAgentsPanelButton'
import {OpenAlertsPanelButton} from './toolbar/OpenAlertsPanelButton'
import {ReviewMenuButton} from './toolbar/ReviewMenuButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {RefreshButton} from './toolbar/RefreshButton'
import {useIsComparisonOutdated} from '../stores/layout-selectors'
import {MiniHeaderMoreMenu} from './MiniHeaderMoreMenu'
import {PullRequestHeadBranchName} from './PullRequestHeadBranchName'
import {RocketIcon, ArrowUpRightIcon, CommentIcon} from '@primer/octicons-react'

function getStatusObj(state: PullRequestState) {
  switch (state) {
    case PullRequestState.Merged:
      return {text: 'merged', datetimeKey: 'mergedTime' as const}
    case PullRequestState.Closed:
      return {text: 'closed', datetimeKey: 'closedTime' as const}
    default:
      return {text: 'opened', datetimeKey: 'createdTime' as const}
  }
}

export type PullRequestMiniHeaderProps = HeaderPageData & {
  activeRoute: PullRequestActiveRoute
}

/**
 * LivePullRequestMiniHeader is a thin wrapper around PullRequestMiniHeader that calls `useHeaderPageData` to automatically
 * update the header when the alive channel is triggered with `{title_updated: true}`, `{git_updated: true}`, or when
 * there is an update to the PullRequestState channel. This lives here instead of the base page so that only the header
 * re-renders on cache updates, not the entire page.
 */
export function LivePullRequestMiniHeader({
  aliveChannels,
  bannersData,
  activeRoute,
  featureStates,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
  viewSettings,
}: PullRequestHeaderProps) {
  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData,
    featureStates,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
  })
  return <PullRequestMiniHeader {...headerData} activeRoute={activeRoute} />
}

export function PullRequestMiniHeader({
  pullRequest,
  repository,
  mergeStatusButtonData,
  aliveChannels,
  activeRoute,
  stack,
}: PullRequestMiniHeaderProps) {
  const {isSticky, sensorRef} = useStickyHeader()
  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const hasValidatedMergeData = validatedMergeData !== null

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  const {text: statusText, datetimeKey} = getStatusObj(pullRequest.state)
  const datetime = pullRequest[datetimeKey]

  useHydrateMarkerCounts()
  const showMarkerCounts = isFeatureEnabled('prs_header_marker_counts')

  return (
    <>
      <div className={clsx(stickyStyles.stickyHeader, styles.miniHeader)}>
        <div className={styles.miniHeaderInner}>
          <div className={styles.metadata}>
            <StateIcon state={pullRequest.state} />
            <span className={clsx(styles.number, 'f5 fgColor-muted')}>#{pullRequest.number}</span>
            {isSticky && (
              <>
                <a href="#top" className={clsx(styles.title, 'f5 text-bold fgColor-default')}>
                  <VerifiedHTMLText className="markdown-title" html={pullRequest.titleHtml} />
                </a>
                <span className={styles.headBranchName}>
                  <span className="sr-only">Head branch:</span>
                  <PullRequestHeadBranchName
                    baseRepositoryOwnerLogin={repository.ownerLogin}
                    headBranch={pullRequest.headBranch}
                    headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                    headRepositoryName={pullRequest.headRepositoryName}
                    isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  />
                </span>
              </>
            )}
            {!isSticky && datetime && (
              <span className="f5 fgColor-muted">
                {statusText} <RelativeTime datetime={datetime} />
              </span>
            )}
          </div>
          <div className={styles.actions}>
            {validatedMergeData && (
              <MiniHeaderMergeOrRefresh
                validatedMergeData={validatedMergeData}
                isDockedMode={isDockedMode}
                onToggleDockedPanel={handleToggleDockedMergePanel}
                mergeStatusButtonRef={mergeStatusButtonRef}
                pullRequestAliveChannel={aliveChannels.pullRequest}
              />
            )}
            {currentUserLogin && (
              <ReviewMenuButton
                currentUserLogin={currentUserLogin}
                pullRequest={pullRequest}
                headRefOid={pullRequest.headSha}
                repository={repository}
                overlayPortal="external"
                redirectOnMutation={activeRoute !== 'conversation'}
                stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
                size="medium"
              />
            )}
            <PullRequestCodeButton
              codespacesEnabled={repository.codespacesEnabled}
              headBranch={pullRequest.headBranch}
              isEnterprise={repository.isEnterprise}
              pullRequestId={pullRequest.id}
              pullRequestNumber={pullRequest.number}
              repository={repository}
              size="medium"
              iconOnly
            />
            <MiniHeaderMoreMenu size="medium" />
            <div className={styles.panelButtons}>
              <div className={clsx(styles.divider, 'hide-sm')} />
              <OpenOverviewPanelButton
                aliveChannel={aliveChannels.pullRequest}
                className="hide-sm hide-md"
                size="medium"
              />
              {isFeatureEnabled('coding_agent_logs_in_pr') && (
                <OpenAgentsPanelButton
                  repository={repository}
                  pullRequestNumber={pullRequest.number}
                  className="hide-sm hide-md"
                  size="medium"
                />
              )}
              <OpenCommentsPanelButton
                aliveChannels={aliveChannels}
                className="hide-sm"
                buttonClasses={!showMarkerCounts ? styles.squareButton : undefined}
                showMarkerCounts={showMarkerCounts}
                size="medium"
              />
              <OpenAlertsPanelButton
                endCommitOid={pullRequest.headSha}
                className="hide-sm"
                buttonClasses={!showMarkerCounts ? styles.squareButton : undefined}
                showMarkerCounts={showMarkerCounts}
                size="medium"
              />
              {isFeatureEnabled('copilot_chat_docked_panel') && <OpenCopilotChatPanelButton size="medium" />}
              <MiniHeaderFeedbackMenu />
            </div>
          </div>
        </div>
      </div>
      <div ref={sensorRef} className={styles.stickyHeaderSentinel} />
    </>
  )
}

function MiniHeaderMergeOrRefresh({
  validatedMergeData,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  pullRequestAliveChannel,
}: {
  validatedMergeData: ValidatedMergeStatusButtonData | null
  isDockedMode: boolean
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: React.RefObject<HTMLButtonElement | null>
  pullRequestAliveChannel: string
}) {
  const isComparisonOutdated = useIsComparisonOutdated()

  return (
    <>
      {isLoggedIn() && !isComparisonOutdated && validatedMergeData && (
        <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
          <MergeStatusButtonWrapper
            channels={validatedMergeData.channels}
            headSha={validatedMergeData.headSha}
            helpUrl={validatedMergeData.helpUrl}
            viewerLogin={validatedMergeData.viewerLogin}
            isDockedMode={isDockedMode}
            onToggleDockedPanel={onToggleDockedPanel}
            mergeStatusButtonRef={mergeStatusButtonRef}
            variant="invisible"
            size="medium"
            buttonClasses="hide-sm"
          />
          {isDockedMode && (
            <MergeStatusPanel
              helpUrl={validatedMergeData.helpUrl}
              viewerLogin={validatedMergeData.viewerLogin}
              channels={validatedMergeData.channels}
            />
          )}
        </MergeMethodContextProvider>
      )}
      <RefreshButton aliveChannel={pullRequestAliveChannel} />
    </>
  )
}

function MiniHeaderFeedbackMenu() {
  return (
    <ActionMenu>
      <ActionMenu.Button variant="invisible" size="medium" className="hide-sm hide-md">
        <Label variant="success">Private preview</Label>
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem
            href="https://gh.io/prs-modern-header-changelog"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActionList.LeadingVisual>
              <RocketIcon />
            </ActionList.LeadingVisual>
            What&apos;s new
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
          <ActionList.LinkItem
            href="https://gh.io/prs-modern-header-feedback"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActionList.LeadingVisual>
              <CommentIcon />
            </ActionList.LeadingVisual>
            Give feedback
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
